import React from "react";
import { Box, Typography, Container, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import SearchBar from "../../common/SearchBar/SearchBar";
import Logo from "../../shared/Logo";
import {
  useMembership,
  FREE_TIER_TICKER,
} from "../../../contexts/MembershipContext";
import { isNative } from "../../../lib/platform";
import { getSymbolImageUrl } from "../../../lib/symbolImage";
import {
  NativeLandingContainer,
  NativeLandingHeader,
  NativeListRow,
  NativeSectionLabel,
} from "../../Native/NativeLanding";
import { Stack } from "@mui/material";

// Logos go through our own proxy, never the provider's CDN — the proxy is
// cached and survives a provider outage.
const EXAMPLE_COMPANIES = [
  { ticker: "NVDA", name: "NVIDIA Corporation", logo: getSymbolImageUrl("NVDA") },
  { ticker: "META", name: "Meta Platforms, Inc.", logo: getSymbolImageUrl("META") },
  { ticker: "AAPL", name: "Apple Inc.", logo: getSymbolImageUrl("AAPL") },
  { ticker: "MSFT", name: "Microsoft Corporation", logo: getSymbolImageUrl("MSFT") },
  { ticker: "GOOG", name: "Alphabet Inc.", logo: getSymbolImageUrl("GOOG") },
];

interface EarningsSearchHeroProps {
  /**
   * Renders beneath other content rather than as the whole page: drops the
   * full-viewport height and the large top offset, both of which assume
   * nothing sits above it.
   */
  embedded?: boolean;
}

const EarningsSearchHero: React.FC<EarningsSearchHeroProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isMembershipActive } = useMembership();
  const isDark = theme.palette.mode === "dark";

  const handleCompanyClick = (ticker: string) => {
    navigate(`/earnings/company/${ticker}`);
  };

  // Native app: compact left-aligned header (no logo), search, and dense
  // company rows — vertically centred between the app bars.
  if (isNative()) {
    return (
      <NativeLandingContainer fillViewport={!embedded}>
        {!embedded && (
          <NativeLandingHeader
            title="Earnings"
            subtitle="Transcripts, dates and estimates for any company"
          />
        )}

        <SearchBar
          placeholder="Search any stock ticker or company..."
          width="100%"
          variant="chatgpt"
          mode="earnings"
          disabled={!isMembershipActive}
        />

        <NativeSectionLabel>Popular earnings</NativeSectionLabel>

        <Stack spacing={1.25}>
          {EXAMPLE_COMPANIES.map((company) => (
            <NativeListRow
              key={company.ticker}
              leading={
                <Logo src={company.logo} alt={`${company.name} logo`} ticker={company.ticker} size={28} />
              }
              title={company.name}
              subtitle={company.ticker}
              onClick={() => handleCompanyClick(company.ticker)}
              disabled={!isMembershipActive && company.ticker !== FREE_TIER_TICKER}
            />
          ))}
        </Stack>
      </NativeLandingContainer>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          // dvh, and a proportional rather than fixed top offset: under the
          // native shell the tab bar is already reserved, so 100vh overflowed
          // by exactly its height.
          minHeight: embedded ? "auto" : "100dvh",
          gap: { xs: 4, md: 5 },
          pt: embedded ? 0 : { xs: "18dvh", md: "24dvh" },
          pb: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 4, md: 6 },
            width: "100%",
            maxWidth: 800,
            textAlign: "center",
          }}
        >
          {/* Logo — page furniture, so it goes when embedded under a header */}
          {!embedded && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: { xs: 2, md: 3 },
              }}
            >
              <img
                src="/insightLongLogoPrimaryWhiteText.svg"
                alt="Insight Analytics Logo"
                style={{
                  height: 64,
                  width: "auto",
                  maxWidth: "100%",
                }}
              />
            </Box>
          )}

          {/* Search Bar */}
          <Tooltip
            title={isMembershipActive ? "" : "Available with Insight Pro"}
            arrow
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 600,
                textAlign: "center",
                "& .MuiAutocomplete-root .MuiOutlinedInput-root input": {
                  textAlign: "center",
                },
              }}
            >
              <SearchBar
                placeholder="Search any stock ticker or company name..."
                width="100%"
                variant="chatgpt"
                mode="earnings"
                disabled={!isMembershipActive}
              />
            </Box>
          </Tooltip>
        </Box>

        {/* Example Companies */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 1100,
            mx: "auto",
          }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 2, md: 3 } }}>
            <Typography
              sx={{
                fontSize: { xs: "0.9375rem", md: "1.125rem" },
                fontWeight: 600,
                color: isDark ? "#ffffff" : "#0f172a",
                mb: 0.5,
              }}
            >
              Popular earnings
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "0.75rem", md: "0.875rem" },
                fontWeight: 400,
                color: isDark ? "#9ca3af" : "#64748b",
              }}
            >
              Quick access to trending earnings transcripts
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 2, md: 3 },
              justifyContent: "center",
              alignItems: "center",
              py: 1,
              "& > *": {
                flexBasis: {
                  xs: "100%",
                  sm: "calc(50% - 12px)",
                  md: "calc(33.333% - 16px)",
                },
                maxWidth: {
                  xs: "320px",
                  sm: "320px",
                  md: "320px",
                },
              },
            }}
          >
            {EXAMPLE_COMPANIES.map((company) => {
              const isEnabled =
                isMembershipActive || company.ticker === FREE_TIER_TICKER;

              const card = (
                <Box
                  key={company.ticker}
                  onClick={() => isEnabled && handleCompanyClick(company.ticker)}
                  sx={{
                    cursor: isEnabled ? "pointer" : "default",
                    opacity: isEnabled ? 1 : 0.5,
                    transition: "all 0.2s ease",
                    backgroundColor: isDark
                      ? "rgba(255, 255, 255, 0.04)"
                      : "rgba(0, 0, 0, 0.04)",
                    border: isDark
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.1)",
                    borderRadius: 3,
                    px: { xs: 2.5, md: 3 },
                    py: { xs: 1.5, md: 2 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 1.5, md: 2 },
                    width: { xs: "280px", md: "320px" },
                    minWidth: "280px",
                    ...(isEnabled && {
                      "&:hover": {
                        backgroundColor: isDark
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.08)",
                        borderColor: isDark
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                        transform: "scale(1.02)",
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                    }),
                  }}
                >
                  <Logo
                    src={company.logo}
                    alt={`${company.name} logo`}
                    ticker={company.ticker}
                    size={20}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8125rem", md: "0.875rem" },
                      fontWeight: 500,
                      color: isDark ? "#ffffff" : "#1d1d1f",
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      flex: 1,
                    }}
                  >
                    {company.name}
                  </Typography>
                </Box>
              );

              if (!isEnabled) {
                return (
                  <Tooltip
                    key={company.ticker}
                    title="Available with Insight Pro"
                    arrow
                  >
                    {card}
                  </Tooltip>
                );
              }

              return card;
            })}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default EarningsSearchHero;
